import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { Shifts } from "../../types/shiftsTypes";
import api from "../../utils/api";

interface ShiftsState {
  allShifts: Shifts[];
  loading: boolean;
  error: string | null;
}

const initialState: ShiftsState = {
  allShifts: [],
  loading: false,
  error: null,
};

// CREATE SHIFT
export const createShift = createAsyncThunk(
  "shifts/create",
  async (
    shiftData: { shiftName: string; startTime: string; endTime: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(
        `/allocation-tool/shifts/add/new-shift`,
        shiftData
      );
      return res.data.data as Shifts; // returns created shift
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to create shift"
        );
      }
      return rejectWithValue("Failed to create shift");
    }
  }
);

// GET ALL SHIFTS
export const fetchAllShifts = createAsyncThunk(
  "shifts/fetchall",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/allocation-tool/shifts/get/all-shifts`
      );
      return res.data.data as Shifts[];
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to fetch all shifts"
        );
      }
      return rejectWithValue("Failed to fetch all shifts");
    }
  }
);

// UPDATE SHIFT
export const updateShift = createAsyncThunk(
  "shifts/update",
  async (
    shiftData: {
      shiftId: number;
      shiftName: string;
      startTime: string;
      endTime: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.put(
        `/allocation-tool/shifts/update/shift`,
        shiftData
      );
      return res.data.data as Shifts;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to update shift"
        );
      }
      return rejectWithValue("Failed to update shift");
    }
  }
);

// DELETE SHIFT
export const deleteShift = createAsyncThunk(
  "shifts/delete",
  async (shiftId: number, { rejectWithValue }) => {
    try {
      await api.delete(
        `/allocation-tool/shifts/delete/shift`,
        { data: { shiftId } }
      );

      return shiftId;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to delete shift"
        );
      }
      return rejectWithValue("Failed to delete shift");
    }
  }
);

const shiftSlice = createSlice({
  name: "shift",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // FETCH SHIFTS
      .addCase(fetchAllShifts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllShifts.fulfilled, (state, action) => {
        state.loading = false;
        state.allShifts = action.payload;
      })
      .addCase(fetchAllShifts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // CREATE SHIFT
      .addCase(createShift.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createShift.fulfilled, (state, action) => {
        state.loading = false;
        state.allShifts.push(action.payload); // add new shift to list
      })
      .addCase(createShift.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // UPDATE SHIFT
      .addCase(updateShift.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateShift.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        state.allShifts = state.allShifts.map((s) =>
          s.shift_id === updated.shift_id ? updated : s
        );
      })
      .addCase(updateShift.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // DELETE SHIFT
      .addCase(deleteShift.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteShift.fulfilled, (state, action) => {
        state.loading = false;
        state.allShifts = state.allShifts.filter(
          (s) => s.shift_id !== action.payload
        );
      })
      .addCase(deleteShift.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default shiftSlice.reducer;
